/**
 * AI Code Governance Dashboard
 *
 * Route: /code-governance | Nav: Code > AI Code Governance
 *
 * Main dashboard showing enforcement gate activity:
 * - Summary metrics (repos, PRs evaluated, blocked, proof)
 * - Recent enforcement decisions with consequence-driven language
 * - Top triggered policies from the AI policy pack
 * - Repo coverage status
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  DocumentBulletListRegular,
  PlugConnectedRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  impactLine: {
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  decisionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  decisionContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalXXS,
    flex: 1,
  },
  policyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  policyInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flex: 1,
  },
  repoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

type Decision = 'BLOCK' | 'WARN' | 'PASS';

interface EnforcementDecision {
  id: string;
  decision: Decision;
  prTitle: string;
  repo: string;
  impact: string;
  findingsCount: number;
  evaluatedAt: string;
}

const recentDecisions: EnforcementDecision[] = [
  {
    id: 'd1',
    decision: 'BLOCK',
    prTitle: 'feat: add Stripe payment integration',
    repo: 'leftshield/platform-api',
    impact: 'Would have exposed production credentials in source — Stripe secret key hardcoded on line 42',
    findingsCount: 3,
    evaluatedAt: '2026-03-18T09:14:00Z',
  },
  {
    id: 'd2',
    decision: 'BLOCK',
    prTitle: 'fix: admin dashboard auth flow',
    repo: 'leftshield/portal',
    impact: 'Would have allowed unauthenticated access to admin endpoints — authorization middleware bypassed',
    findingsCount: 2,
    evaluatedAt: '2026-03-18T08:45:00Z',
  },
  {
    id: 'd3',
    decision: 'WARN',
    prTitle: 'chore: update logging configuration',
    repo: 'leftshield/platform-api',
    impact: 'PII fields (email, phone) included in structured log output — may violate data retention policy',
    findingsCount: 1,
    evaluatedAt: '2026-03-17T16:30:00Z',
  },
  {
    id: 'd4',
    decision: 'PASS',
    prTitle: 'feat: add input validation to signup form',
    repo: 'leftshield/portal',
    impact: 'All checks passed — validated inputs, no sensitive data exposure, test coverage present',
    findingsCount: 0,
    evaluatedAt: '2026-03-17T14:22:00Z',
  },
  {
    id: 'd5',
    decision: 'BLOCK',
    prTitle: 'feat: dynamic report generator',
    repo: 'transformr/backend-services',
    impact: 'Would have executed untrusted user input via eval() — remote code execution risk',
    findingsCount: 4,
    evaluatedAt: '2026-03-17T11:05:00Z',
  },
];

interface TriggeredPolicy {
  key: string;
  name: string;
  triggerCount: number;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM';
}

const topPolicies: TriggeredPolicy[] = [
  { key: 'AI-SEC-001', name: 'Hardcoded Secret / Token Pattern', triggerCount: 14, severity: 'CRITICAL' },
  { key: 'AI-AUTH-001', name: 'Authorization Bypass Risk', triggerCount: 11, severity: 'CRITICAL' },
  { key: 'AI-APP-001', name: 'Unvalidated External Input', triggerCount: 9, severity: 'HIGH' },
  { key: 'AI-DATA-001', name: 'Sensitive Data Logging', triggerCount: 7, severity: 'CRITICAL' },
  { key: 'AI-DB-001', name: 'Database Query Without Parameterization', triggerCount: 6, severity: 'HIGH' },
];

interface RepoStatus {
  name: string;
  provider: string;
  protected: boolean;
  lastEvaluated: string | null;
}

const repos: RepoStatus[] = [
  { name: 'leftshield/platform-api', provider: 'GitHub', protected: true, lastEvaluated: '2026-03-18T09:14:00Z' },
  { name: 'leftshield/portal', provider: 'GitHub', protected: true, lastEvaluated: '2026-03-18T08:45:00Z' },
  { name: 'transformr/backend-services', provider: 'Azure DevOps', protected: true, lastEvaluated: '2026-03-17T11:05:00Z' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDecisionColor = (decision: Decision): 'danger' | 'warning' | 'success' => {
  switch (decision) {
    case 'BLOCK': return 'danger';
    case 'WARN': return 'warning';
    case 'PASS': return 'success';
  }
};

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'important' => {
  switch (severity) {
    case 'CRITICAL': return 'danger';
    case 'HIGH': return 'important';
    case 'MEDIUM': return 'warning';
    default: return 'warning';
  }
};

const formatTime = (iso: string): string => {
  return new Date(iso).toLocaleString();
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CodeGovernanceDashboardPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Code Governance"
        subtitle="Enforcement gate for AI-assisted code in the path to production"
      />

      {/* Summary Metrics */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <PlugConnectedRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Connected Repos</Body2>
              <Title2 className={ps.metricValue}>3</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <DocumentBulletListRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>PRs Evaluated (7d)</Body2>
              <Title2 className={ps.metricValue}>47</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Blocked (7d)</Body2>
              <Title2 className={ps.metricValue} style={{ color: tokens.colorPaletteRedForeground1 }}>12</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Proof Generated</Body2>
              <Title2 className={ps.metricValue}>47</Title2>
            </div>
          </div>
        </Card>
      </div>

      {/* Two-column: Recent Decisions + Top Policies */}
      <div className={ps.twoColumn}>
        {/* Recent Enforcement Decisions */}
        <Card>
          <CardHeader
            header={<Title2>Recent Enforcement Decisions</Title2>}
            description="Latest PR evaluations with outcome and impact"
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              {recentDecisions.map((d) => (
                <div key={d.id} className={styles.decisionRow}>
                  <Badge
                    appearance="filled"
                    color={getDecisionColor(d.decision)}
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    {d.decision}
                  </Badge>
                  <div className={styles.decisionContent}>
                    <Body1 style={{ fontWeight: 500 }}>{d.prTitle}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{d.repo}</Caption1>
                    <Body2 className={styles.impactLine}>{d.impact}</Body2>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalXXS }}>
                      <Caption1>{d.findingsCount} finding{d.findingsCount !== 1 ? 's' : ''}</Caption1>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{formatTime(d.evaluatedAt)}</Caption1>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Top Triggered Policies */}
        <Card>
          <CardHeader
            header={<Title2>Top Triggered Policies</Title2>}
            description="Most frequently violated AI governance rules (7d)"
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              {topPolicies.map((p) => (
                <div key={p.key} className={styles.policyRow}>
                  <div className={styles.policyInfo}>
                    <Body2 style={{ fontFamily: 'monospace', fontWeight: 600, flexShrink: 0 }}>{p.key}</Body2>
                    <Body1>{p.name}</Body1>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Badge appearance="outline" color={getSeverityColor(p.severity)} size="small">
                      {p.severity}
                    </Badge>
                    <Body2 className={ps.metricValue} style={{ fontWeight: 600 }}>{p.triggerCount}</Body2>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Repo Coverage */}
      <Card>
        <CardHeader
          header={<Title2>Repo Coverage</Title2>}
          description="Repositories connected to the AI code governance gate"
        />
        <div className={ps.cardBody}>
          <div className={ps.rowList}>
            {repos.map((r) => (
              <div key={r.name} className={styles.repoRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                  <Body1 style={{ fontWeight: 500 }}>{r.name}</Body1>
                  <Badge appearance="outline" size="small">{r.provider}</Badge>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                  {r.lastEvaluated && (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Last evaluated {formatTime(r.lastEvaluated)}
                    </Caption1>
                  )}
                  <Badge
                    appearance="filled"
                    color={r.protected ? 'success' : 'danger'}
                  >
                    {r.protected ? 'Protected' : 'Unprotected'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
